/**
 * Configuration store for managing system-wide settings.
 * This store is initialized at application startup and provides
 * access to system configuration throughout the application.
 *
 * The store maintains a single source of truth for system configuration
 * and is typically initialized during the application bootstrap process.
 * The configuration persists across component re-renders and page navigations
 * until explicitly updated.
 */

'use client';

import { create } from 'zustand';
import type { SystemConfig } from '@/config/serverConfig';

/**
 * Configuration state interface.
 * Defines the shape of the configuration store's state and actions.
 */
interface ConfigState {
  /** Flag indicating if the store has been initialized */
  isInitialized: boolean;

  /** System configuration object containing all system-wide settings */
  systemConfig: SystemConfig | null;

  /**
   * Updates the system configuration and marks the store as initialized.
   * @param config - The new system configuration
   */
  setSystemConfig: (config: SystemConfig) => void;

  /**
   * Gets the base URL without trailing slashes.
   * @returns The base URL without trailing slashes, or empty string if not configured
   */
  getBaseUrlNoTrailingSlash: () => string;
}

/**
 * Configuration store hook.
 * Provides access to system configuration state and actions.
 *
 * @example
 * function MyComponent() {
 *   const systemConfig = useConfigStore(state => state.systemConfig);
 *   return systemConfig ? <div>{systemConfig.orgName}</div> : null;
 * }
 */
export const useConfigStore = create<ConfigState>()((set, get) => ({
  isInitialized: false,
  systemConfig: null,
  setSystemConfig: (config) => set({ systemConfig: config, isInitialized: true }),
  getBaseUrlNoTrailingSlash: () => {
    const config = get().systemConfig;
    return config?.apiEndpoint.replace(/\/+$/, '') ?? '';
  },
}));
